// Talks to delta-force/server. One Google sign-in a day: the Firebase ID token is
// traded for a day-long session token, which is what every other call carries.
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getAuthInstance } from '../firebase';

const SESSION_KEY = 'df:session';

export const API_BASE = (() => {
  if (process.env.REACT_APP_DF_API) return process.env.REACT_APP_DF_API.replace(/\/+$/, '');
  try {
    const o = localStorage.getItem('df:api');
    if (o) return o.replace(/\/+$/, '');
  } catch {}
  return window.location.hostname === 'localhost' ? 'http://localhost:8790' : 'https://df.rohitrrao.com';
})();

const read = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
};
const write = s => {
  try { s ? localStorage.setItem(SESSION_KEY, JSON.stringify(s)) : localStorage.removeItem(SESSION_KEY); } catch {}
};

export function getSession() {
  const s = read();
  if (!s) return null;
  if (s.expires_at && Date.now() > s.expires_at) { write(null); return null; }
  return s;
}

export class ApiError extends Error {
  constructor(message, status) { super(message); this.status = status; }
}

export async function api(path, { method = 'GET', body, signal } = {}) {
  const s = getSession();
  let res;
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      method, signal,
      headers: { ...(s ? { Authorization: `Bearer ${s.token}` } : {}), ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    throw new ApiError('server unreachable', 0);
  }
  if (res.status === 401 && s) { write(null); }
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.detail || `${res.status}`, res.status);
  return data;
}

export async function signIn() {
  const auth = getAuthInstance();
  if (!auth) throw new ApiError('auth unavailable', 0);
  const cred = await signInWithPopup(auth, new GoogleAuthProvider());
  const idToken = await cred.user.getIdToken();
  const out = await api('/auth/session', { method: 'POST', body: { id_token: idToken } });
  write({ token: out.token, user: out.user, expires_at: Date.now() + out.expires_in_hours * 3600 * 1000 });
  return out.user;
}

// Revokes every session token for this user server-side (all devices), then
// forgets the local one. The revoke is best-effort: if the server is unreachable
// we still clear locally, and the token dies at its 24h expiry regardless.
export async function signOut() {
  try { await api('/auth/signout', { method: 'POST' }); } catch {}
  write(null);
  try { getAuthInstance()?.signOut(); } catch {}
}
